/* ==========================================================================
   Admin Login Page (Global)
   Task F20: Admin login at /admin/login - redirects to portal after login
   PORTAL-199: Admin | Provider switch. Provider sign-in redirects to the
   provider's pharmacy, or lists the pharmacies when there is more than one.
   ========================================================================== */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Checkbox } from '@/components/ui/Checkbox'
import { Alert } from '@/components/ui/Alert'
import { TwoFactorCodeStep } from '@/components/auth/TwoFactorCodeStep'
import { adminLogin, adminLoginTwoFactor } from '@/api/admin'
import { login as providerLogin, loginTwoFactor as providerLoginTwoFactor } from '@/api/auth'
import { isPortalSelection, isTwoFactorChallenge } from '@/api/types'
import type {
  LoginRequest,
  LoginResponse,
  PendingTwoFactorLogin,
  PortalOption,
  PortalSelectionResponse,
  TwoFactorChallengeResponse,
} from '@/api/types'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface FormErrors {
  email?: string
  password?: string
}

type LoginAs = 'admin' | 'provider'

const LOGIN_AS_OPTIONS: { value: LoginAs; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'provider', label: 'Provider' },
]

const HEADINGS: Record<LoginAs, { title: string; subtitle: string }> = {
  admin: { title: 'Admin Portal', subtitle: 'Sign in to manage your pharmacy portal' },
  provider: {
    title: 'Provider Portal',
    subtitle: 'Sign in to send prescriptions to your pharmacy',
  },
}

/* --------------------------------------------------------------------------
   Validation
   -------------------------------------------------------------------------- */

function validateEmail(email: string): string | undefined {
  if (!email.trim()) {
    return 'Email is required'
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address'
  }
  return undefined
}

function validatePassword(password: string): string | undefined {
  if (!password) {
    return 'Password is required'
  }
  return undefined
}

function validateForm(email: string, password: string): FormErrors {
  return {
    email: validateEmail(email),
    password: validatePassword(password),
  }
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function AdminLoginRedirect() {
  const navigate = useNavigate()

  const [loginAs, setLoginAs] = useState<LoginAs>('admin')
  // Form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  // Set when the password was right but a 2FA code is needed (PORTAL-187).
  const [twoFactorPending, setTwoFactorPending] =
    useState<PendingTwoFactorLogin | null>(null)
  // Set when the provider's credentials are valid at several pharmacies.
  const [portalChoices, setPortalChoices] = useState<PortalOption[] | null>(null)
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)

  const handleLoginAsChange = (value: LoginAs) => {
    setLoginAs(value)
    setAuthError(null)
    setTwoFactorPending(null)
    setPortalChoices(null)
  }

  const handleProviderResponse = (
    response: LoginResponse | TwoFactorChallengeResponse | PortalSelectionResponse
  ) => {
    // Any pharmacy list stays set under the 2FA step, so Back returns to it.
    if (isTwoFactorChallenge(response)) {
      setTwoFactorPending({ token: response.two_factor_token, email, rememberMe })
      setLoading(false)
      return
    }
    if (isPortalSelection(response)) {
      setPortalChoices(response.portals)
      setLoading(false)
      return
    }
    navigate(`/rx/${response.portal_slug}/prescribe`, { replace: true })
  }

  const handleBlur = (field: 'email' | 'password') => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    if (field === 'email') {
      setErrors((prev) => ({ ...prev, email: validateEmail(email) }))
    } else {
      setErrors((prev) => ({ ...prev, password: validatePassword(password) }))
    }
  }

  const handleEmailChange = (value: string) => {
    setEmail(value)
    setAuthError(null)
    if (touched.email) {
      setErrors((prev) => ({ ...prev, email: validateEmail(value) }))
    }
  }

  const handlePasswordChange = (value: string) => {
    setPassword(value)
    setAuthError(null)
    if (touched.password) {
      setErrors((prev) => ({ ...prev, password: validatePassword(value) }))
    }
  }

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formErrors = validateForm(email, password)
    setErrors(formErrors)
    setTouched({ email: true, password: true })

    if (formErrors.email || formErrors.password) {
      return
    }

    setLoading(true)
    setAuthError(null)

    try {
      const request: LoginRequest = {
        email,
        password,
        remember_me: rememberMe,
      }
      if (loginAs === 'provider') {
        handleProviderResponse(await providerLogin(request))
        return
      }
      const response = await adminLogin(request)
      if (isTwoFactorChallenge(response)) {
        setTwoFactorPending({ token: response.two_factor_token, email, rememberMe })
        setLoading(false)
        return
      }

      // Redirect to admin dashboard using slug from response
      navigate(`/rx/${response.portal_slug}/admin/dashboard`, { replace: true })
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Login failed. Please check your credentials.'
      setAuthError(message)
      setLoading(false)
    }
  }

  const handleTwoFactorSubmit = async (code: string, rememberDevice: boolean) => {
    if (!twoFactorPending) return
    setLoading(true)
    setAuthError(null)

    try {
      const request = {
        two_factor_token: twoFactorPending.token,
        code,
        remember_device: rememberDevice,
      }
      if (loginAs === 'provider') {
        const response = await providerLoginTwoFactor(
          request,
          twoFactorPending.email,
          twoFactorPending.rememberMe
        )
        navigate(`/rx/${response.portal_slug}/prescribe`, { replace: true })
        return
      }
      const response = await adminLoginTwoFactor(
        request,
        twoFactorPending.email,
        twoFactorPending.rememberMe
      )
      navigate(`/rx/${response.portal_slug}/admin/dashboard`, { replace: true })
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Verification failed. Please try again.'
      if (message.includes('expired')) {
        setTwoFactorPending(null)
      }
      setAuthError(message)
      setLoading(false)
    }
  }

  const handleTwoFactorCancel = () => {
    setTwoFactorPending(null)
    setAuthError(null)
  }

  // Log in again scoped to the chosen pharmacy, so its own status and 2FA apply.
  const handlePortalSelect = async (portalSlug: string) => {
    setLoading(true)
    setSelectedSlug(portalSlug)
    setAuthError(null)

    try {
      handleProviderResponse(
        await providerLogin({
          email,
          password,
          remember_me: rememberMe,
          portal_slug: portalSlug,
        })
      )
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Login failed. Please check your credentials.'
      setAuthError(message)
      setLoading(false)
    }
  }

  const handlePortalCancel = () => {
    setPortalChoices(null)
    setAuthError(null)
  }

  const hasErrors = errors.email || errors.password
  const heading = HEADINGS[loginAs]

  if (twoFactorPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <span className="font-serif text-2xl text-deep-purple">TJM Labs</span>
          </div>
          <Card className="p-6 sm:p-8">
            <TwoFactorCodeStep
              onSubmit={handleTwoFactorSubmit}
              onCancel={handleTwoFactorCancel}
              loading={loading}
              error={authError}
            />
          </Card>
        </div>
      </div>
    )
  }

  if (portalChoices) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <span className="font-serif text-2xl text-deep-purple">TJM Labs</span>
          </div>
          <Card className="p-6 sm:p-8">
            <div className="text-center mb-6">
              <h1 className="font-serif text-2xl sm:text-3xl text-charcoal">
                Choose a pharmacy
              </h1>
              <p className="text-charcoal/70 mt-2">
                Your account is registered with more than one pharmacy
              </p>
            </div>

            {authError && (
              <Alert variant="error" className="mb-6">
                {authError}
              </Alert>
            )}

            <ul className="space-y-3">
              {portalChoices.map((portal) => (
                <li key={portal.portal_slug}>
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    loading={loading && selectedSlug === portal.portal_slug}
                    disabled={loading}
                    onClick={() => handlePortalSelect(portal.portal_slug)}
                    className="w-full"
                  >
                    {portal.pharmacy_name}
                  </Button>
                </li>
              ))}
            </ul>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={handlePortalCancel}
                disabled={loading}
                className="text-sm text-charcoal/60 hover:text-charcoal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender rounded"
              >
                Back
              </button>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/" className="cursor-pointer">
            <span className="font-serif text-2xl text-deep-purple">TJM Labs</span>
          </Link>
        </div>

        <Card className="p-6 sm:p-8">
          {/* Admin | Provider switch */}
          <div
            role="group"
            aria-label="Sign in as"
            className="grid grid-cols-2 gap-1 rounded-lg bg-warm-beige p-1 mb-6"
          >
            {LOGIN_AS_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                aria-pressed={loginAs === value}
                onClick={() => handleLoginAsChange(value)}
                disabled={loading}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender ${
                  loginAs === value
                    ? 'bg-deep-purple text-white shadow-sm'
                    : 'text-charcoal/70 hover:text-charcoal'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal">{heading.title}</h1>
            <p className="text-charcoal/70 mt-2">{heading.subtitle}</p>
          </div>

          {authError && (
            <Alert variant="error" className="mb-6">
              {authError}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-5">
              <FormField
                label="Email"
                required
                error={touched.email ? errors.email : undefined}
                htmlFor="admin-email"
              >
                <Input
                  id="admin-email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={() => handleBlur('email')}
                  error={touched.email && !!errors.email}
                  disabled={loading}
                />
              </FormField>

              <FormField
                label="Password"
                required
                error={touched.password ? errors.password : undefined}
                htmlFor="admin-password"
              >
                <div className="relative">
                  <Input
                    id="admin-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    onBlur={() => handleBlur('password')}
                    error={touched.password && !!errors.password}
                    disabled={loading}
                    className="pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-charcoal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender rounded"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Eye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </FormField>

              <Checkbox
                label="Remember me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              disabled={loading || !!hasErrors}
              className="w-full mt-6"
            >
              Sign In
            </Button>
          </form>

          {loginAs === 'admin' && (
            <div className="mt-6 pt-4 border-t border-charcoal/10 text-center">
              <p className="text-sm text-charcoal/70">
                Don't have an account?{' '}
                <Link
                  to="/admin/signup"
                  className="text-deep-purple hover:underline"
                >
                  Sign up
                </Link>
              </p>
            </div>
          )}
        </Card>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-sm text-charcoal/60 hover:text-charcoal transition-colors"
          >
            Back to home
          </Link>
        </div>
      </div>
    </div>
  )
}

export default AdminLoginRedirect
